import resolveFrontendModule from "@app/resolveModule/frontend";
import Icon from "@components/Atoms/Icon";
import { useRouter } from "@core/Api/useRouter";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import { type GesCloudAdditionalSsoProviderCode, GesCloudApi } from "@ext/enterprise-cloud/GesCloudApi";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { useCallback, useEffect, useState } from "react";
import SpinnerLoader from "../../../../components/Atoms/SpinnerLoader";
import FetchService from "../../../../ui-logic/ApiServices/FetchService";
import { usePlatform } from "../../../../ui-logic/hooks/usePlatform";
import { cn } from "../../../../ui-logic/utils/cn";
import { relocateToUrl } from "../../../enterprise/components/SingInOut/hooks/useSignIn";

type GesCloudDefaultSsoProviderCode = "yandex";
type GesCloudSsoProviderCode = GesCloudDefaultSsoProviderCode | GesCloudAdditionalSsoProviderCode;

interface GesCloudSocialSignInProps {
	className?: string;
	gesCloudUrl?: string;
}

function getGesCloudSignInUrl(gesUrl: string, provider: "google" | "yandex", isTauri: boolean) {
	const url = new URL(gesUrl);
	url.pathname = `/sso/login/${provider}`;

	const from = isTauri ? "http://localhost:52054" : window.location.href;
	url.searchParams.set("from", from);
	if (isTauri) url.searchParams.set("useOneTimeCode", "true");

	return url.toString();
}

const useGesCloudSocialSignInLogic = (gesCloudUrlOverride) => {
	const apiUrlCreator = ApiUrlCreatorService.value;

	const { url: contextGesCloudUrl } = PageDataContextService.value.conf.enterpriseCloud;
	const gesCloudUrl = gesCloudUrlOverride ?? contextGesCloudUrl;
	const router = useRouter();

	const hasGesCloudUrl = Boolean(gesCloudUrl && URL.canParse(gesCloudUrl));
	const [additionalProviders, setAdditionalProviders] = useState<GesCloudAdditionalSsoProviderCode[]>([]);
	const [isAdditionalProvidersLoading, setIsAdditionalProvidersLoading] = useState(false);

	const { environment } = usePlatform();
	const isTauri = environment === "tauri";

	useEffect(() => {
		if (!hasGesCloudUrl) {
			setAdditionalProviders([]);
			setIsAdditionalProvidersLoading(false);
			return;
		}

		let isMounted = true;
		const gesCloudApi = new GesCloudApi(gesCloudUrl);
		setIsAdditionalProvidersLoading(true);

		void gesCloudApi
			.getSsoProviders()
			.then((providers) => {
				if (isMounted) setAdditionalProviders(providers);
			})
			.catch(() => {
				if (isMounted) setAdditionalProviders([]);
			})
			.finally(() => {
				if (isMounted) setIsAdditionalProvidersLoading(false);
			});

		return () => {
			isMounted = false;
		};
	}, [gesCloudUrl, hasGesCloudUrl]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: it's ok
	const executeLogin = useCallback(
		async (url: string) => {
			await FetchService.fetch(apiUrlCreator.getEnableCloudUrl());
			if (isTauri) await resolveFrontendModule("gesCloudLogin")(url, apiUrlCreator, router, gesCloudUrl);
			else relocateToUrl(url);
		},
		[router, isTauri, apiUrlCreator, gesCloudUrl],
	);

	const yandexAuthUrl = hasGesCloudUrl ? getGesCloudSignInUrl(gesCloudUrl, "yandex", isTauri) : null;
	const relocateToYandexAuthUrl = useCallback(() => {
		if (!yandexAuthUrl) return;
		void executeLogin(yandexAuthUrl);
	}, [executeLogin, yandexAuthUrl]);

	const googleAuthUrl = hasGesCloudUrl ? getGesCloudSignInUrl(gesCloudUrl, "google", isTauri) : null;
	const relocateToGoogleAuthUrl = useCallback(() => {
		if (!googleAuthUrl) return;
		void executeLogin(googleAuthUrl);
	}, [executeLogin, googleAuthUrl]);
	const isGoogleAvailable = additionalProviders.includes("google");

	return {
		googleDisabled: !hasGesCloudUrl,
		isAdditionalProvidersLoading,
		isGoogleAvailable,
		onGoogleClick: relocateToGoogleAuthUrl,
		yandexDisabled: !hasGesCloudUrl,
		onYandexClick: relocateToYandexAuthUrl,
	};
};

export const GesCloudSocialSignIn = ({
	className,
	gesCloudUrl: gesCloudUrlOverride,
}: GesCloudSocialSignInProps = {}) => {
	const gesCloudSignInProviderButtons: Record<GesCloudSsoProviderCode, { icon: string; label: string }> = {
		google: {
			icon: "google-icon",
			label: t("enterprise-cloud-guest.buttons.continueWithGoogle"),
		},
		yandex: {
			icon: "yandex",
			label: t("enterprise-cloud-guest.buttons.continueWithYandex"),
		},
	};

	const {
		googleDisabled,
		isAdditionalProvidersLoading,
		isGoogleAvailable,
		onGoogleClick,
		yandexDisabled,
		onYandexClick,
	} = useGesCloudSocialSignInLogic(gesCloudUrlOverride);
	const googleButtonConfig = gesCloudSignInProviderButtons.google;
	const yandexButtonConfig = gesCloudSignInProviderButtons.yandex;

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			<Button disabled={yandexDisabled} onClick={onYandexClick} type="button" variant="outline">
				<Icon code={yandexButtonConfig.icon} />
				{yandexButtonConfig.label}
			</Button>
			{isAdditionalProvidersLoading ? (
				<div className="flex h-10 items-center justify-center" data-qa="ges-cloud-additional-providers-loader">
					<SpinnerLoader height={20} lineWidth={2} width={20} />
				</div>
			) : (
				isGoogleAvailable && (
					<Button disabled={googleDisabled} onClick={onGoogleClick} type="button" variant="outline">
						<Icon code={googleButtonConfig.icon} />
						{googleButtonConfig.label}
					</Button>
				)
			)}
		</div>
	);
};
